import { z } from '@awpmarine/core/zod';
import ky, { type KyInstance } from 'ky';

import { env } from '@/env';

const OkSchema = z.object({ ok: z.literal(true) });

export type ContactRequest = {
  name: string;
  email: string;
  subject: string;
  company?: string | null;
  message: string;
};

/** The public endpoints behind the site's forms; each answers `{ ok: true }` or an error status. */
export class MarketingClient {
  private readonly inner: KyInstance;

  constructor(client: KyInstance) {
    this.inner = client.extend({ prefix: 'marketing' });
  }

  /** Sends the contact form; rejects when the API refuses it or cannot be reached. */
  async contact(json: ContactRequest): Promise<void> {
    await this.inner.post('contact', { json }).json(OkSchema);
  }

  /**
   * Sends a job application or a general expression of interest, CV attached, as multipart form
   * data; rejects the same way.
   */
  async application(body: FormData): Promise<void> {
    await this.inner.post('careers', { body }).json(OkSchema);
  }

  /** Adds the address to the newsletter segment; rejects the same way. */
  async newsletter(email: string): Promise<void> {
    await this.inner.post('newsletter', { json: { email } }).json(OkSchema);
  }
}

/**
 * The site is pre-rendered to static HTML with no server of its own, so its forms post straight to
 * apps/api. The host stays an environment variable (unlike the clients in packages) because each
 * deployment - local, preview, production - talks to its own API.
 */
export class ApiClient {
  private readonly inner: KyInstance;
  readonly marketing: MarketingClient;

  constructor(baseUrl: string = env.VITE_API_URL) {
    this.inner = ky.create({ baseUrl });
    this.marketing = new MarketingClient(this.inner);
  }
}

export const client = new ApiClient();
